import { useMemo } from "react";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import queryString from "query-string";
import { prop, splitEvery, uniqBy } from "ramda";
import { KeyedMutator, SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { useDebounce } from "~/hooks/useDebounce";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";

const CHUNK_SIZE = 100;
const { VITE_CATALOG_API } = import.meta.env;

const useDatasetTablesSearch = (searchTerm: string): SWRResponse<DatasetTableResponse[]> => {
  const debouncedSearch = useDebounce(searchTerm);
  const { data: fqns = [] } = useDatasetTableFQNs();
  const shouldSearch = Boolean(debouncedSearch && fqns.length);

  // Split table FQN list into chunks to avoid 414/431 error code response
  // due to a single request url becoming too lengthy after a certain point when sent together
  const chunks = splitEvery(CHUNK_SIZE, fqns);

  const getKey = (chunkIndex: number): string | null => {
    if (!shouldSearch || !chunks.length || !chunks[chunkIndex]) {
      return null;
    }

    const filters = {
      query: {
        bool: {
          must: {
            terms: { fullyQualifiedName: chunks[chunkIndex] },
          },
        },
      },
    };

    const qs = queryString.stringify({
      index: "catalog-api-table_search_index",
      q: debouncedSearch,
      query_filter: JSON.stringify(filters),
      // Use ES max limit
      size: 10000,
    });
    return `${VITE_CATALOG_API}/search/query?${qs}`;
  };

  const responses = useSWRInfinite<SearchResponse<TableEntity>>(
    getKey,
    (url: string) => fetch(url).then((res) => res.json()),
    {
      parallel: true,
      initialSize: chunks.length,
    }
  );

  const memoizedData = useMemo(() => {
    const mappedData = responses.data
      ?.flatMap((response) => response.hits.hits)
      .sort((a, b) => b._score - a._score)
      .map(({ _source: entity }) => ({
        id: entity.id,
        name: entity.name,
        description: entity.description ?? "",
        fullyQualifiedName: entity.fullyQualifiedName,
        database_schema: entity.databaseSchema.name,
        updated_at: new Date(entity.updatedAt).toISOString(),
        columns: entity.columns,
        deleted: false,
      }));

    if (mappedData) {
      return uniqBy(prop("fullyQualifiedName"), mappedData);
    }
  }, [responses.data]);

  return {
    ...responses,
    isLoading: responses.isLoading || Boolean(searchTerm && searchTerm !== debouncedSearch),
    mutate: responses.mutate as KeyedMutator<any>,
    data: memoizedData,
  };
};

export default useDatasetTablesSearch;
